import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { insurersService, type InsurersParams } from '@/services/insurers';
import type { Insurer } from '@/types/admin';

export function useInsurers(params?: InsurersParams) {
  return useQuery({
    queryKey: ['insurers', params],
    queryFn: () => insurersService.list(params),
  });
}

export function useIntegrationStatus() {
  return useQuery({
    queryKey: ['insurers', 'integration-status'],
    queryFn: () => insurersService.integrationStatus(),
  });
}

export function useInsurer(id: string) {
  return useQuery({
    queryKey: ['insurers', id],
    queryFn: () => insurersService.get(id),
    enabled: !!id,
  });
}

export function useInsurersLookup(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['insurers', 'lookup'],
    queryFn: () => insurersService.lookup(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled ?? true,
  });
}

export function useCreateInsurer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Insurer>) => insurersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurers'] });
    },
  });
}

export function useUpdateInsurer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Insurer> }) =>
      insurersService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurers'] });
    },
  });
}

export function useDeleteInsurer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => insurersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurers'] });
    },
  });
}

export function useActivateInsurer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => insurersService.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurers'] });
    },
  });
}

export function usePauseInsurer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => insurersService.pause(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurers'] });
    },
  });
}

export function useBlockInsurer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => insurersService.block(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurers'] });
    },
  });
}

export function useInsurerStats() {
  return useQuery({
    queryKey: ['insurers', 'stats'],
    queryFn: () => insurersService.stats(),
  });
}

export function useCreateInsurerUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      insurerId,
      data,
    }: {
      insurerId: string;
      data: Parameters<typeof insurersService.createUser>[1];
    }) => insurersService.createUser(insurerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurers'] });
    },
  });
}

// Employees
export function useInsurerEmployees(insurerId?: string) {
  return useQuery({
    queryKey: ['insurers', 'employees', insurerId],
    queryFn: () => insurersService.listEmployees({ insurer: insurerId }),
    enabled: !!insurerId,
  });
}

// API Configs
export function useInsurerAPIConfigs(insurerId?: string) {
  return useQuery({
    queryKey: ['insurers', 'api-configs', insurerId],
    queryFn: () => insurersService.listAPIConfigs({ insurer: insurerId }),
    enabled: !!insurerId,
  });
}
